import React from 'react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { ratingChanges, upgradeStats, peerCountries } from '../../../data/peerData';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell, ReferenceLine } from 'recharts';

// Central Asia & Silk Road Corridor colors
const countryColors: Record<string, string> = {
  UZB: '#00d26a',  // Primary - Green
  KAZ: '#fbbf24',  // Kazakhstan - Amber
  KGZ: '#00a3ff',  // Kyrgyzstan - Blue
  TJK: '#00d4d4',  // Tajikistan - Cyan
  TKM: '#f97316',  // Turkmenistan - Orange
  AZE: '#ef4444',  // Azerbaijan - Red
  GEO: '#a855f7',  // Georgia - Purple
  MNG: '#6b7280'   // Mongolia - Gray
};

const getMomentumStyle = (momentum: string): string => {
  const styles: Record<string, string> = {
    'ACCELERATING': 'text-data-green',
    'STABLE': 'text-data-amber',
    'SLOWING': 'text-data-amber',
    'FLAT': 'text-gray-400',
    'DECLINING': 'text-data-red',
    'NOT RATED': 'text-gray-500'
  };
  return styles[momentum] || 'text-gray-400';
};

const getDirectionStyle = (direction: string): string => {
  const styles: Record<string, string> = {
    'upgrade': 'bg-data-green/20 text-data-green',
    'downgrade': 'bg-data-red/20 text-data-red',
    'affirm': 'bg-gray-600/20 text-gray-400'
  };
  return styles[direction] || 'bg-gray-600/20 text-gray-400';
};

interface RatingMomentumPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const RatingMomentumPanel: React.FC<RatingMomentumPanelProps> = ({ isMinimized, onMinimize }) => {
  // Prepare data for bar chart
  const barData = Object.entries(upgradeStats).map(([code, stats]) => ({
    code,
    upgrades: stats.upgrades,
    downgrades: -stats.downgrades,
    net: stats.upgrades - stats.downgrades,
    momentum: stats.momentum
  })).sort((a, b) => b.net - a.net);

  // Recent rating actions (last 6)
  const recentActions = [...ratingChanges]
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 6);

  return (
    <Panel title="RATING MOMENTUM" subtitle="Credit Rating Trends" status="live" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full gap-2">
        {/* Upgrade/Downgrade Bar Chart */}
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} margin={{ top: 10, right: 10, bottom: 5, left: -10 }} layout="vertical">
              <XAxis
                type="number"
                domain={[-4, 5]}
                tick={{ fontSize: 8, fill: '#6b7280' }}
                axisLine={{ stroke: '#1e1e26' }}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey="code"
                tick={{ fontSize: 9, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
                width={35}
              />
              <ReferenceLine x={0} stroke="#374151" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#0a0a0f',
                  border: '1px solid #00d26a',
                  borderRadius: '6px',
                  fontSize: '11px',
                  padding: '10px 14px',
                  boxShadow: '0 4px 20px rgba(0, 210, 106, 0.15)',
                }}
                labelStyle={{ color: '#ffffff', fontWeight: 'bold', marginBottom: '6px' }}
                formatter={(value: number, name: string) => {
                  if (name === 'upgrades') return [value, 'Upgrades'];
                  if (name === 'downgrades') return [Math.abs(value), 'Downgrades'];
                  return [value, name];
                }}
              />
              <Bar dataKey="upgrades" stackId="a" radius={[0, 4, 4, 0]}>
                {barData.map((entry) => (
                  <Cell key={`up-${entry.code}`} fill={countryColors[entry.code]} fillOpacity={0.8} />
                ))}
              </Bar>
              <Bar dataKey="downgrades" stackId="a" radius={[4, 0, 0, 4]}>
                {barData.map((entry) => (
                  <Cell key={`down-${entry.code}`} fill="#ef4444" fillOpacity={0.7} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Momentum Summary */}
        <div className="grid grid-cols-4 gap-1">
          {barData.slice(0, 4).map(d => {
            const stats = upgradeStats[d.code as keyof typeof upgradeStats];
            const country = peerCountries.find(c => c.code === d.code);
            return (
              <div key={d.code} className="bg-terminal-bg rounded p-1.5">
                <div className="flex items-center justify-between mb-0.5">
                  <span style={{ color: countryColors[d.code] }} className="text-xs font-medium">{d.code}</span>
                  <span className={`text-xs font-bold ${d.net > 0 ? 'text-data-green' : d.net < 0 ? 'text-data-red' : 'text-gray-400'}`}>
                    {d.net > 0 ? '+' : ''}{d.net}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">{country?.creditRating}</span>
                  <span className={getMomentumStyle(stats.momentum)}>{stats.momentum}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Recent Actions Timeline */}
        <div className="bg-terminal-bg rounded p-2">
          <div className="text-xs text-gray-500 uppercase mb-1">Recent Rating Actions</div>
          <div className="space-y-1">
            {recentActions.map((action, i) => (
              <div key={i} className="flex items-center text-xs gap-2">
                <span className="text-gray-500 w-14">{action.date}</span>
                <span style={{ color: countryColors[action.country] }} className="w-8 font-medium">{action.country}</span>
                <span className="text-gray-400 w-12">{action.agency}</span>
                <span className="text-gray-500">{action.oldRating}</span>
                <span className="text-gray-500">→</span>
                <span className={action.direction === 'upgrade' ? 'text-data-green' : action.direction === 'downgrade' ? 'text-data-red' : 'text-white'}>
                  {action.newRating}
                </span>
                <span className={`px-1 py-0 rounded text-xs ${getDirectionStyle(action.direction)}`}>
                  {action.direction.toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="pt-2 border-t border-terminal-border flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.SP} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default RatingMomentumPanel;
